import type { RuleNode, RuleNodeType } from "../types/nodes";

const NODE_TYPE_LABEL: Partial<Record<RuleNodeType, string>> = {
	reaction: "reaction",
	clear_reaction: "clear reaction",
	query_builder: "query builder",
	filter: "filter",
	sort: "sort",
	assignment: "assignment",
	decision: "decision",
	loop: "loop",
	event_listener: "event listener",
	action: "action",
	stop: "stop",
};

/**
 * When the trimmed name matches another node of the same type (excluding `excludeNodeId`),
 * returns a user-facing validation message; otherwise `undefined`.
 * Comparison is case-insensitive so "MyNode" and "mynode" are treated as the same name.
 */
export function getDuplicateNameAmongSameNodeTypeError(
	nodes: RuleNode[],
	nodeType: RuleNodeType,
	excludeNodeId: string | null | undefined,
	candidateName: string,
): string | undefined {
	if (!excludeNodeId) return undefined;
	const trimmed = candidateName.trim().toLowerCase();
	if (!trimmed) return undefined;
	const conflict = nodes.some(
		(n) =>
			n.type === nodeType &&
			n.id !== excludeNodeId &&
			typeof n.data?.name === "string" &&
			n.data.name.trim().toLowerCase() === trimmed,
	);
	if (!conflict) return undefined;
	const label = NODE_TYPE_LABEL[nodeType] ?? nodeType;
	return `This name is already used by another ${label} node.`;
}

export const DUPLICATE_REACTION_ASSIGNMENT_DECISION_NAME_MESSAGE =
	"This name is already used by another reaction, assignment, or decision node.";

/**
 * Graph-wide uniqueness for reaction / assignment / decision display names (all depths).
 * When the trimmed name matches another such node (excluding the active node's current name), returns a message.
 */
export function getDuplicateReactionAssignmentDecisionNameError(
	allNames: Set<string>,
	activeNodeId: string | null | undefined,
	findNode: (id: string) => RuleNode | undefined,
	candidateName: string,
): string | undefined {
	const trimmed = candidateName.trim().toLowerCase();
	if (!trimmed) return undefined;
	const active = activeNodeId ? findNode(activeNodeId) : undefined;
	const activeName = typeof active?.data?.name === "string" ? active.data.name.trim().toLowerCase() : null;
	if (trimmed === activeName) return undefined;
	if (allNames.has(trimmed)) return DUPLICATE_REACTION_ASSIGNMENT_DECISION_NAME_MESSAGE;
	return undefined;
}
